import { useEffect, useState } from 'react'
import Toast from 'components/toast/Toast'
import { IconChecklistCircle } from 'components/icons'
import { AxiosError } from 'axios'
import {
  AdminsApi,
  AdminsOrdersDeliveriesUpdateRequest,
  Configuration,
  DeliveryEnum,
  EcommerceApi,
  EcommerceOperationApi,
  EcommerceProductTypeEnum,
  OrderDirectionEnum,
  OrderOperationSortFieldEnum,
  OrdersListOperationResponseItem,
  OrdersUpdateStatusEnum,
  SaleOrderStatusEnum,
} from 'generated/api'
import { mapPaymentMethod, mapStatusDeliveryMethod, mapStatusPayment } from 'helpers/wording'
import { axiosDefaultConfig } from 'constants/axios'
import { enumToastMessage } from 'constants/toast'

const axiosConfig = new Configuration(axiosDefaultConfig)
const ecommerceOperationApi = new EcommerceOperationApi(axiosConfig)
const ecommerceApi = new EcommerceApi(axiosConfig)
const adminsApi = new AdminsApi(axiosConfig)

export const useOrders = (orderType: EcommerceProductTypeEnum) => {
  const [statusFetch, setStatusFetch] = useState<'loading' | 'idle' | 'failed'>('loading')
  const [orders, setOrders] = useState<OrdersListOperationResponseItem[]>([])
  const [totalRows, setTotalRows] = useState(0)
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(10)
  const [searchKeyword, setSearchKeyword] = useState('')
  const [status, setStatus] = useState<SaleOrderStatusEnum>(SaleOrderStatusEnum.New)
  const [sortField, setSortField] = useState<OrderOperationSortFieldEnum>('createdAt')
  const [sortDirection, setSortDirection] = useState<OrderDirectionEnum>('DESC')
  const [selectedOrder, setSelectedOrder] = useState<OrdersListOperationResponseItem | null>(null)
  const [statusFetchModal, setStatusFetchModal] = useState<'loading' | 'idle' | 'failed'>('idle')
  const [isShowConfirmPopup, setIsShowConfirmPopup] = useState(false)
  const [isShowChangePickUpPopup, setIsShowChangePickUpPopup] = useState(false)
  const { toast, toastContainer } = Toast()

  useEffect(() => {
    fetchOrders()
  }, [page, limit, status, sortField, sortDirection, orderType])

  const fetchOrders = async (): Promise<void> => {
    try {
      setStatusFetch('loading')
      const { data } = await ecommerceOperationApi.ordersListOperation(
        page,
        limit,
        status === ('' as SaleOrderStatusEnum) ? undefined : (status as SaleOrderStatusEnum),
        searchKeyword === '' ? undefined : searchKeyword,
        sortField,
        sortDirection,
        orderType,
      )
      setOrders(data.items)
      setTotalRows(data.pagination.total)
      setStatusFetch('idle')
    } catch (e) {
      setStatusFetch('failed')
    }
  }

  const updateStatusOrder = async () => {
    if (selectedOrder) {
      const productType: EcommerceProductTypeEnum = selectedOrder.orderItems[0].productType
      let statusPayload: OrdersUpdateStatusEnum | '' = ''

      if (productType === EcommerceProductTypeEnum.Digital) {
        statusPayload = OrdersUpdateStatusEnum.Completed
      } else {
        if (selectedOrder.status === SaleOrderStatusEnum.New) {
          statusPayload = OrdersUpdateStatusEnum.Confirmed
        } else if (selectedOrder.status === SaleOrderStatusEnum.Confirmed) {
          if (selectedOrder.orderDelivery.method === DeliveryEnum.Pickup) {
            statusPayload = OrdersUpdateStatusEnum.ReadyToPickUp
          } else if (selectedOrder.orderDelivery.method === DeliveryEnum.Delivery) {
            statusPayload = OrdersUpdateStatusEnum.Delivering
          }
        } else if (selectedOrder.status === SaleOrderStatusEnum.Delivering) {
          statusPayload = OrdersUpdateStatusEnum.Completed
        }
      }

      if (statusPayload) {
        return await ecommerceApi.ordersUpdateStatus(selectedOrder?.id ?? '', 'ADMIN_WEB', {
          status: statusPayload,
        })
      }
    }
    return true
  }

  const updatePickUpDetail = async (payload: AdminsOrdersDeliveriesUpdateRequest) => {
    if (selectedOrder) {
      const updatePayload = {
        recipientName: payload.recipientName,
        recipientPhoneNo: payload.recipientPhoneNo,
        vehicleLicenseNo: payload.vehicleLicenseNo,
      }
      return await adminsApi.adminsOrdersDeliveriesCreate(selectedOrder?.id, '', updatePayload)
    }
    return true
  }

  const changeSortOrders = (field: OrderOperationSortFieldEnum) => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'ASC' ? 'DESC' : 'ASC')
    } else {
      setSortField(field)
      setSortDirection('ASC')
    }
  }

  const changeStatusOrders = (status: SaleOrderStatusEnum) => {
    setPage(1)
    setStatus(status)
  }

  const changeSearchKeywordOrders = (keyword: string) => {
    setSearchKeyword(keyword)
  }

  const enterSearchKeywordOrders = () => {
    if (page === 1) fetchOrders()
    else setPage(1)
  }

  const changeLimitPageOrders = (limit: number) => {
    setPage(1)
    setLimit(limit)
  }

  const changePageOrders = (page: number) => {
    setPage(page)
  }

  const selectOrder = (order: OrdersListOperationResponseItem | null) => {
    setSelectedOrder(order)
  }

  const openConfirmModal = () => {
    setIsShowConfirmPopup(true)
  }

  const openChangePickUpModal = () => {
    setIsShowChangePickUpPopup(true)
  }

  const cancelConfirmModal = () => {
    setIsShowConfirmPopup(false)
  }

  const cancelChangePickUpModal = () => {
    setIsShowChangePickUpPopup(false)
  }

  const acceptConfirmOrderModal = async () => {
    try {
      setStatusFetchModal('loading')
      await updateStatusOrder()
      setStatusFetchModal('idle')
      setIsShowConfirmPopup(false)
      setSelectedOrder(null)
      await toast(
        `Nomor pesanan ${selectedOrder?.id} atas nama ${selectedOrder?.userName} ${
          selectedOrder?.status === SaleOrderStatusEnum.New
            ? 'sedang di proses.'
            : 'sudah siap diambil.'
        }`,
        'success',
        <IconChecklistCircle color="white" />,
      )
      await fetchOrders()
    } catch (err) {
      await toast('Tidak dapat terhubung ke server, silakan mencoba kembali.', 'danger')
      setStatusFetchModal('failed')
    }
  }

  const acceptChangePickUpModal = async (values: AdminsOrdersDeliveriesUpdateRequest) => {
    try {
      setStatusFetchModal('loading')
      await updatePickUpDetail(values)
      setStatusFetchModal('idle')
      setIsShowChangePickUpPopup(false)
      setSelectedOrder(null)
      await toast(
        'Detail Penjemputan berhasil dirubah',
        'success',
        <IconChecklistCircle color="white" />,
      )
      await fetchOrders()
    } catch (err) {
      setStatusFetchModal('failed')
      const error = err as AxiosError
      const responseStatus = error.response?.status
      const expectedResponseStatus = [400, 404]
      if (responseStatus && expectedResponseStatus.includes(responseStatus)) {
        if (responseStatus === 404) {
          await toast('Nomor pesanan tidak ditemukan', 'danger')
        } else {
          await toast(enumToastMessage.err_400, 'danger')
        }
      } else {
        await toast('Tidak dapat terhubung ke server, silakan mencoba kembali.', 'danger')
      }
    }
  }

  const isOrderAllowedChangePickup = () => {
    if (selectedOrder) {
      const allowedStatus: SaleOrderStatusEnum[] = [
        SaleOrderStatusEnum.Confirmed,
        SaleOrderStatusEnum.ReadyToPickUp,
      ]
      const allowedOrderDeliveryToEdit = !!selectedOrder.orderDelivery.method
      return allowedStatus.includes(selectedOrder.status) && allowedOrderDeliveryToEdit
    }
    return false
  }

  const selectedOrderPaymentDetail = () => {
    if (selectedOrder) {
      return [
        {
          label: 'Nomor Invoice',
          value: selectedOrder.invoiceNo,
        },
        {
          label: 'Metode Pembayaran',
          value:
            selectedOrder.paymentItems && selectedOrder.paymentItems.length > 0
              ? selectedOrder.paymentItems
                  .map(item => `${mapPaymentMethod(item.method)}`)
                  .join(', ')
              : '-',
        },
        {
          label: 'Status Pembayaran',
          value: selectedOrder.paymentStatus ? mapStatusPayment(selectedOrder.paymentStatus) : '-',
        },
      ]
    }
    return []
  }

  const selectedOrderDeliveryDetail = () => {
    if (selectedOrder) {
      return [
        {
          label: 'Metode Pengiriman',
          value: selectedOrder.orderDelivery?.method
            ? mapStatusDeliveryMethod(selectedOrder.orderDelivery?.method)
            : '-',
        },
        {
          label: 'Lokasi',
          value: selectedOrder.storeName || '-',
        },
      ]
    }
    return []
  }

  const selectedOrderRecipientDetail = () => {
    if (selectedOrder) {
      return [
        {
          label: 'Nama',
          value: selectedOrder.orderDelivery?.recipientName || '-',
        },
        {
          label: 'Nomor Handphone',
          value: selectedOrder.orderDelivery?.recipientPhoneNo || '-',
        },
        {
          label: 'Nomor Plat',
          value: selectedOrder.orderDelivery?.vehicleLicenseNo || '-',
        },
      ]
    }
    return []
  }

  return {
    toastContainer,
    statusFetch,
    statusFetchModal,
    selectedOrder,
    orders,
    totalRows,
    limit,
    page,
    searchKeyword,
    status,
    sortField,
    sortDirection,
    isShowConfirmPopup,
    isShowChangePickUpPopup,
    changeSearchKeywordOrders,
    changeStatusOrders,
    changeLimitPageOrders,
    changePageOrders,
    changeSortOrders,
    enterSearchKeywordOrders,
    fetchOrders,
    selectOrder,
    isOrderAllowedChangePickup,
    selectedOrderPaymentDetail,
    selectedOrderDeliveryDetail,
    selectedOrderRecipientDetail,
    openConfirmModal,
    openChangePickUpModal,
    cancelConfirmModal,
    cancelChangePickUpModal,
    acceptConfirmOrderModal,
    acceptChangePickUpModal,
  }
}
